import React from "react";
import { ClerkProvider, useSession, SignIn, SignUp } from "@clerk/clerk-react";
import { i18n } from "../lib/i18n";

const colors = {
  primary: "#287379",
  primaryHover: "#1F585C",
  secondary: "#8B7EF7",
  secondaryHover: "#7A6DF6",
};

const ctaColor = colors.primary;
const ctaHoverColor = colors.primaryHover;

const AuthComponent: React.FC<{
  redirectUrl?: string;
  ctaColor?: string;
  ctaHoverColor?: string;
  subtitle?: string;
  showSpinner?: boolean;
  onLoaded?: () => void;
}> = ({
  redirectUrl,
  ctaColor,
  ctaHoverColor,
  subtitle,
  showSpinner = true,
  onLoaded,
}) => {
  const { isLoaded } = useSession();

  // Notify the host (Stimulus controller) once Clerk is ready, so a
  // server-rendered spinner can be swapped for the form without a gap.
  React.useEffect(() => {
    if (isLoaded) onLoaded?.();
  }, [isLoaded, onLoaded]);

  // Reserve a stable area so the rest of the page doesn't jump when Clerk
  // swaps the spinner for the (taller) form, and crossfade between the two so
  // the form eases in instead of popping. When showSpinner is false the
  // pre-load state is left empty (e.g. a server-rendered spinner already covers
  // it), avoiding a double spinner.
  // Cap the wrapper height while Clerk is loading so the spinner area stays a
  // sensible size; drop the cap once Clerk has rendered so the form is free to
  // take its natural height (sign-up forms in particular grow taller).
  return (
    <div className={`relative content-center ${isLoaded ? "" : "max-h-[372px]"}`}>
      {showSpinner && (
        <div
          className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
            isLoaded ? "pointer-events-none opacity-0" : "opacity-100"
          }`}
        >
          <span
            style={{ color: ctaColor }}
            className="loading loading-spinner loading-lg animate-spin"
          ></span>
        </div>
      )}

      <div
        className={`flex justify-center transition-opacity duration-300 ${
          isLoaded ? "opacity-100" : "opacity-0"
        }`}
      >
        {isLoaded && (
          <SignIn
            appearance={{
              elements: {
                cardBox: "mx-auto max-w-full bg-base-100 border border-base-300 shadow-sm",
                headerTitle: "text-lg font-semibold text-left",
                // Hide Clerk's subtitle slot unless a subtitle is supplied.
                headerSubtitle: subtitle
                  ? "text-sm text-base-content/60 text-left"
                  : "hidden",
                formButtonPrimary: `btn btn-primary `,
                footerActionLink: `text-${ctaColor} hover:text-${ctaHoverColor}`,
              },
            }}
            fallbackRedirectUrl={redirectUrl}
            forceRedirectUrl={redirectUrl}
            signUpForceRedirectUrl={redirectUrl}
            signInUrl={`/sign-in${redirectUrl ? `?redirect_url=${redirectUrl}` : ""}`}
            signUpUrl={`/sign-up${redirectUrl ? `?redirect_url=${redirectUrl}` : ""}`}
            withSignUp={true}
          />
        )}
      </div>
    </div>
  );
};

export const ClerkAuth: React.FC<{
  publishableKey: string;
  redirectUrl?: string;
  subtitle?: string;
  showSpinner?: boolean;
  onLoaded?: () => void;
}> = ({
  publishableKey,
  redirectUrl,
  subtitle,
  showSpinner = true,
  onLoaded,
}) => {
  if (!publishableKey) {
    console.error("Missing publishableKey");
    return (
      <div className="text-red-500">
        {i18n.t("clerk_auth.missing_publishable_key")}
      </div>
    );
  }

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      localization={{
        signIn: {
          start: {
            titleCombined: i18n.t("clerk_auth.sign_in_title"),
            ...(subtitle ? { subtitleCombined: subtitle } : {}),
          },
        },
      }}
      appearance={{
        baseTheme: undefined,
        variables: {
          colorPrimary: ctaColor,
          colorTextOnPrimaryBackground: "white",
        },
      }}
    >
      <AuthComponent
        redirectUrl={redirectUrl}
        ctaColor={ctaColor}
        ctaHoverColor={ctaHoverColor}
        subtitle={subtitle}
        showSpinner={showSpinner}
        onLoaded={onLoaded}
      />
    </ClerkProvider>
  );
};
